import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, from, firstValueFrom } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  login(email: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      map((auth: AuthModel) => {
        this.setAuthFromLocalStorage(auth);
      }),
      switchMap(() => this.getUserByToken()),
      catchError(() => {
        this.clearAuthState();
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  changePassword(password: string, confirmPassword: string): Observable<void> {
    return this.authHttpService.changePassword(password, confirmPassword);
  }

  logout(): void {
    this.clearAuthState();
    this.router.navigate(['/auth/login']);
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth?.accessToken) {
      return of(undefined);
    }

    if (this.isTokenExpired(auth.accessToken) && this.isTokenExpired(auth.refreshToken)) {
      this.clearAuthState();
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.accessToken).pipe(
      map((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
        } else {
          this.clearAuthState();
        }
        return user;
      }),
      catchError(() => {
        this.clearAuthState();
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  getAccessToken(): string | undefined {
    return this.getAuthFromLocalStorage()?.accessToken;
  }

  getRefreshToken(): string | undefined {
    return this.getAuthFromLocalStorage()?.refreshToken;
  }

  isTokenExpired(token: string, bufferSeconds: number = 30): boolean {
    try {
      const payload = JSON.parse(this.decodeBase64Url(token.split('.')[1] || ''));
      const exp = payload.exp as number | undefined;
      if (!exp) return true;
      return exp * 1000 < Date.now() + bufferSeconds * 1000;
    } catch {
      return true;
    }
  }

  private refreshAccessTokenPromise: Promise<string | null> | null = null;

  refreshAccessToken(): Observable<string | null> {
    if (this.refreshAccessTokenPromise) {
      return from(this.refreshAccessTokenPromise);
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken || this.isTokenExpired(refreshToken)) {
      return of(null);
    }

    this.refreshAccessTokenPromise = firstValueFrom(
      this.authHttpService.refreshToken(refreshToken).pipe(
        map((response) => {
          this.updateAccessToken(response.accessToken);
          return response.accessToken;
        })
      )
    ).finally(() => {
      this.refreshAccessTokenPromise = null;
    }) as Promise<string | null>;

    return from(this.refreshAccessTokenPromise);
  }

  getUserRoles(): string[] {
    const token = this.getAccessToken();

    if (!token) {
      return [];
    }

    try {
      const payload = JSON.parse(this.decodeBase64Url(token.split('.')[1] || '')) as Record<string, unknown>;
      const roleClaim = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
        ?? payload['role']
        ?? payload['roles'];

      if (Array.isArray(roleClaim)) {
        return roleClaim.filter((role): role is string => typeof role === 'string');
      }

      return typeof roleClaim === 'string' ? [roleClaim] : [];
    } catch {
      return [];
    }
  }

  hasAnyRole(roles: string[]): boolean {
    if (!roles.length) {
      return true;
    }

    const currentRoles = this.getUserRoles();
    return roles.some((role) => currentRoles.includes(role));
  }

  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    if (auth.accessToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }

      return JSON.parse(lsValue) as AuthModel;
    } catch {
      return undefined;
    }
  }

  private clearAuthState(): void {
    localStorage.removeItem(this.authLocalStorageToken);
    this.currentUserSubject.next(undefined);
  }

  private updateAccessToken(accessToken: string): void {
    const auth = this.getAuthFromLocalStorage();
    if (auth) {
      auth.accessToken = accessToken;
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      this.currentUserSubject.next(this.currentUserSubject.value);
    }
  }

  private decodeBase64Url(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4;
    const padded = padding ? normalized.padEnd(normalized.length + (4 - padding), '=') : normalized;

    return atob(padded);
  }
}
